// ═══════════════════════════════════════════════════════════════════════
// Contrôle de la carte employé (personnel habilité, permission controler_cartes_employes) — accès à l'API
// /api/cartes (backend/src/routes/cartesRoutes.js, services/carteEmploye.js) :
//   • POST /api/cartes/controle { qr: "SGC1.…" } → { resultat, identite (VALIDE seulement), statut } — JSON direct ;
//   • GET  /api/cartes/controles/:id/photo → photo du salarié, pour l'auteur d'un contrôle VALIDE, 5 minutes.
// Le serveur est seul juge (habilitation, population, anti-rejeu) ; ses refus sont rendus tels quels.
// ═══════════════════════════════════════════════════════════════════════
import { useMemo } from 'react';
import axios from 'axios';
import { CheckCircle2, Clock, CircleHelp, Ban, ShieldX, MapPinOff, Repeat } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

export const PERMISSION_CONTROLE = 'controler_cartes_employes';
/** Longueur maximale d'un QR acceptée par le serveur (rappelée pour guider la saisie). */
export const LONGUEUR_QR_MAX = 512;
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const CHEMIN_PHOTO = new RegExp(`^/api/cartes/controles/(${UUID})/photo$`, 'i');

function client(token) {
  const entetes = token ? { Authorization: `Bearer ${token}` } : {};
  return {
    /** Contrôle d'un QR lu ou saisi. Réponse en JSON direct (ou enveloppée : lecture tolérante). */
    controler: async (qr) => {
      const d = (await axios.post('/api/cartes/controle', { qr }, { headers: entetes })).data;
      return d && d.data && d.data.resultat ? d.data : d;
    },
    /** Photo de contrôle (route réservée à l'auteur du contrôle, 5 minutes) : Blob. Chemin accepté : /api/cartes/controles/<id>/photo. */
    photo: async (chemin) => {
      if (!CHEMIN_PHOTO.test(String(chemin || ''))) throw new Error('chemin de photo inattendu');
      return (await axios.get(chemin, { headers: entetes, responseType: 'blob' })).data;
    },
  };
}

/** Client du contrôle de carte pour le composant courant. */
export function useCarte() {
  const { token } = useAuth();
  return useMemo(() => client(token), [token]);
}

/**
 * Numéro du contrôle (identifiant rh.cartes_controles) : champ `controle_id` s'il est renvoyé (demande DM-WEB-004),
 * sinon lu dans l'adresse de la photo de contrôle ; null s'il n'est pas communiqué.
 */
export function numeroControle(r) {
  if (!r) return null;
  const direct = r.controle_id || r.controle?.id || null;
  if (direct && new RegExp(`^${UUID}$`, 'i').test(String(direct))) return String(direct);
  const m = CHEMIN_PHOTO.exec(String(r.identite?.photo_url_controle || ''));
  return m ? m[1] : null;
}

/**
 * Résultats du contrôle : ton (couleur), mot affiché en grand, explication et conduite à tenir.
 * La couleur n'est jamais seule : chaque résultat a son mot et son icône.
 */
export const RESULTATS = {
  VALIDE: { ton: 'succes', Icone: CheckCircle2, mot: 'CARTE VALIDE', texte: 'La carte est authentique, active et lue à l\'instant. Vérifiez que la photo correspond à la personne présente.' },
  DEJA_CONTROLE: { ton: 'alerte', Icone: Repeat, mot: 'QR DÉJÀ UTILISÉ', texte: 'Ce QR a déjà servi à un contrôle. Demandez au salarié d\'afficher sa carte à nouveau (le QR change toutes les 30 secondes), puis relisez-le.' },
  EXPIRE: { ton: 'alerte', Icone: Clock, mot: 'QR EXPIRÉ', texte: 'Le QR n\'est valable qu\'une minute. Demandez au salarié de rouvrir sa carte dans l\'application, puis relisez-le.' },
  SIGNATURE_INVALIDE: { ton: 'danger', Icone: ShieldX, mot: 'QR NON AUTHENTIQUE', texte: 'Ce QR n\'est pas une carte SOGAS authentique, ou il a été mal lu. Relisez-le ; si le résultat se répète, refusez la carte et prévenez le service RH.' },
  CARTE_REVOQUEE: { ton: 'danger', Icone: Ban, mot: 'CARTE RÉVOQUÉE', texte: 'Cette carte n\'est plus valable. Refusez-la et orientez la personne vers le service RH.' },
  INCONNUE: { ton: 'danger', Icone: CircleHelp, mot: 'CARTE INCONNUE', texte: 'Aucune carte ne correspond à ce QR. Refusez-la et orientez la personne vers le service RH.' },
  HORS_PERIMETRE: { ton: 'neutre', Icone: MapPinOff, mot: 'HORS DE VOTRE PÉRIMÈTRE', texte: 'Ce salarié ne fait pas partie de la population qui vous est confiée : le contrôle revient à un agent de son site.' },
};
/** Couleurs des tons (variables du thème : fond doux, bord, texte). */
export const COULEURS = {
  succes: { fond: 'var(--sg-succes-doux)', bord: 'var(--sg-succes)', texte: 'var(--sg-succes)' },
  alerte: { fond: 'var(--sg-alerte-doux)', bord: 'var(--sg-alerte)', texte: 'var(--sg-alerte)' },
  danger: { fond: 'var(--sg-danger-doux)', bord: 'var(--sg-danger)', texte: 'var(--sg-danger)' },
  neutre: { fond: 'var(--sg-neutre-doux)', bord: 'var(--sg-bord-fort)', texte: 'var(--sg-texte)' },
};
